import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  InputAdornment,
  Chip,
  Alert,
  CircularProgress,
  useTheme,
  Grid,
  alpha,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
} from '@mui/material'
import {
  Search,
  Assessment as AssessmentIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material'

import { RootState, AppDispatch } from '../app/store'
import { fetchAssessments, fetchAssessmentById } from '../features/assessments/assessmentSlice'
import { fetchUserEnrollments } from '../features/assignments/assignmentSlice'
import WelcomeCard from '../components/WelcomeCard'
import ShowResultsDialog from '../components/ShowResultsDialog'

const ModernAssessmentCard: React.FC<{ 
  assessment: any
  enrollment?: any
  onStart: (assessmentId: number) => void
  onViewScore: (assessmentId: number) => void
  isStarting: boolean
}> = ({ assessment, enrollment, onStart, onViewScore, isStarting }) => {
  const theme = useTheme()

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty?.toLowerCase()) {
      case 'beginner': return theme.palette.success.main
      case 'intermediate': return theme.palette.warning.main
      case 'advanced': return theme.palette.error.main
      case 'easy': return theme.palette.success.main
      case 'medium': return theme.palette.warning.main
      case 'hard': return theme.palette.error.main
      default: return theme.palette.grey[500]
    }
  }

  return (
    <Card
      sx={{
        height: 100,
        display: 'flex',
        flexDirection: 'row',
        position: 'relative',
        overflow: 'hidden',
        '&:hover': {
          boxShadow: theme.shadows[4],
        },
      }}
    >
      {/* Left Icon Section */}
      <Box
        sx={{
          width: 80,
          background: `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, ${theme.palette.secondary.dark} 100%)`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <AssessmentIcon sx={{ fontSize: 28, color: 'white' }} />
      </Box>

      {/* Main Content Area */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, p: 1, position: 'relative' }}>
        {/* Title and Description */}
        <Box sx={{ flex: 1 }}>
          <Typography 
            variant="subtitle2" 
            fontWeight="bold" 
            sx={{
              fontSize: '0.95rem',
              lineHeight: 1.2,
              mb: 0.5,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {assessment.title}
          </Typography>

          <Typography 
            variant="body2" 
            color="text.secondary" 
            sx={{
              fontSize: '0.75rem',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              mb: 0.5,
            }}
          >
            {assessment.description || 'Test your knowledge'}
          </Typography>
        </Box>

        {/* Bottom Row - Chips and Button */}
        <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
          <Box display="flex" alignItems="center" gap={0.5}>
            <Chip
              label={assessment.difficulty || 'Medium'}
              size="small"
              sx={{
                bgcolor: getDifficultyColor(assessment.difficulty) + '20',
                color: getDifficultyColor(assessment.difficulty),
                fontWeight: 600,
                fontSize: '0.6rem',
                height: 18,
                '& .MuiChip-label': { px: 0.75 }
              }}
            />
            <Chip
              label={enrollment?.submission_status === 'graded' ? 'Completed' : 'Available'}
              size="small"
              variant="outlined"
              sx={{
                fontSize: '0.6rem',
                height: 18,
                '& .MuiChip-label': { px: 0.75 }
              }}
            />
          </Box>

          {/* Action Button */}
          {enrollment?.submission_status === 'graded' ? (
            <Button
              variant="contained"
              size="small"
              onClick={() => onViewScore(assessment.id)}
              sx={{
                borderRadius: '4px',
                px: 2,
                py: 0.5,
                fontSize: '0.7rem',
                textTransform: 'none',
                minWidth: 'auto',
                height: 26,
              }}
            >
              View Score
            </Button>
          ) : (
            <Button
              variant="contained"
              size="small"
              onClick={() => onStart(assessment.id)}
              disabled={isStarting}
              sx={{
                borderRadius: '4px',
                px: 2,
                py: 0.5,
                fontSize: '0.7rem',
                textTransform: 'none',
                minWidth: 'auto',
                height: 26,
              }}
            >
              {isStarting ? 'Loading...' : 'Start'}
            </Button>
          )}
        </Box>
      </Box>
    </Card>
  )
}

const ModernAssessmentCatalogPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>()
  const navigate = useNavigate()
  const theme = useTheme()
  const { assessments, isLoading, error } = useSelector((state: RootState) => state.assessments)
  // Backend now returns only current user's enrollments via only_mine=true parameter
  const { userEnrollments } = useSelector((state: RootState) => state.assignments)
  const { user } = useSelector((state: RootState) => state.auth)

  const [searchTerm, setSearchTerm] = useState('')
  const [startingAssessmentId, setStartingAssessmentId] = useState<string | null>(null)
  const [scoreDialogOpen, setScoreDialogOpen] = useState(false)
  const [selectedAssessment, setSelectedAssessment] = useState<any>(null)
  const [showResultsDialogOpen, setShowResultsDialogOpen] = useState(false)
  const [selectedResultsAssessment, setSelectedResultsAssessment] = useState<{ id: number; title: string } | null>(null)

  useEffect(() => {
    dispatch(fetchAssessments())
    dispatch(fetchUserEnrollments())
  }, [dispatch])

  const handleStartAssessment = (assessmentId: number) => {
    setStartingAssessmentId(assessmentId.toString())
    navigate(`/assessments/${assessmentId}`)
  }

  const handleViewScore = async (assessmentId: number) => {
    // Fetch fresh assessment data with submission status
    try {
      const assessment = await dispatch(fetchAssessmentById(assessmentId.toString())).unwrap()
      if (assessment) {
        setSelectedAssessment(assessment)
        setScoreDialogOpen(true)
      }
    } catch (error) {
      console.error('Failed to fetch assessment details:', error)
      // Fallback to cached data
      const assessment = assessments.find(a => a.id === assessmentId)
      if (assessment) {
        setSelectedAssessment(assessment)
        setScoreDialogOpen(true)
      }
    }
  }

  const handleCloseDialog = () => {
    setScoreDialogOpen(false)
    setSelectedAssessment(null)
  }

  const handleRetakeAssessment = () => {
    if (selectedAssessment) {
      handleCloseDialog()
      navigate(`/assessments/${selectedAssessment.id}`)
    }
  }

  const handleShowResults = (assessmentId: number, assessmentTitle: string) => {
    setSelectedResultsAssessment({ id: assessmentId, title: assessmentTitle })
    setShowResultsDialogOpen(true)
  }

  const handleCloseResultsDialog = () => {
    setShowResultsDialogOpen(false)
    setSelectedResultsAssessment(null)
  }

  // Get enrollment for an assessment - filter by is_standalone_assessment
  const getEnrollmentForAssessment = (assessmentId: number) => {
    return (userEnrollments || []).find(ue => 
      ue.assignment?.assessment && 
      ue.is_standalone_assessment === true && // Only standalone assessments
      (typeof ue.assignment.assessment === 'object' 
        ? ue.assignment.assessment.id === assessmentId
        : parseInt(ue.assignment.assessment.toString()) === assessmentId)
    )
  }

  // Get all user-enrolled standalone assessments
  const userEnrolledAssessments = (assessments || []).filter(assessment => {
    const enrollment = getEnrollmentForAssessment(assessment.id)
    return enrollment !== undefined
  })

  // Filter assessments based on search
  const filteredAssessments = userEnrolledAssessments.filter(assessment => {
    const matchesSearch = assessment.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (assessment.description || '').toLowerCase().includes(searchTerm.toLowerCase())
    return matchesSearch
  })

  // Categorize assessments by status
  const availableAssessments = filteredAssessments.filter(assessment => {
    const enrollment = getEnrollmentForAssessment(assessment.id)
    return enrollment && enrollment.submission_status !== 'graded'
  })

  const completedAssessments = filteredAssessments.filter(assessment => {
    const enrollment = getEnrollmentForAssessment(assessment.id)
    return enrollment && enrollment.submission_status === 'graded'
  })

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress size={50} />
      </Box>
    )
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Welcome Card */}
      <Box mb={3}>
        <WelcomeCard userName={user?.first_name || 'Student'} showLogo={true} />
      </Box>

      {/* Header Section */}
      <Box mb={4}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          Assessment Center
        </Typography>
        <Typography variant="body1" color="text.secondary" mb={3}>
          Test your knowledge and track your progress with our comprehensive assessments
        </Typography>

        {/* Stats Cards */}
        <Grid container spacing={2} mb={4}>
          <Grid item xs={12} sm={4}>
            <Card sx={{ bgcolor: alpha(theme.palette.secondary.main, 0.1), border: 'none' }}>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h3" fontWeight="bold" color="secondary.main">
                  {userEnrolledAssessments.length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Assessments
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card sx={{ bgcolor: alpha(theme.palette.info.main, 0.1), border: 'none' }}>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h3" fontWeight="bold" color="info.main">
                  {availableAssessments.length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Available
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card sx={{ bgcolor: alpha(theme.palette.success.main, 0.1), border: 'none' }}>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h3" fontWeight="bold" color="success.main">
                  {completedAssessments.length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Completed
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Search */}
        <TextField
          fullWidth
          placeholder="Search assessments..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search color="action" />
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '12px',
            },
          }}
        />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: '12px' }}>
          {error}
        </Alert>
      )}

      {/* Available Assessments Section */}
      {(() => {
        return (
          <Box mb={4}>
            <Typography 
              variant="h5" 
              fontWeight="bold" 
              mb={2} 
              sx={{ color: theme.palette.secondary.main }}
            >
              Available Assessments ({availableAssessments.length})
            </Typography>
            {availableAssessments.length > 0 ? (
              <Grid container spacing={3}>
                {availableAssessments.map((assessment) => (
                  <Grid item xs={12} sm={6} md={4} key={assessment.id}>
                    <ModernAssessmentCard
                      assessment={assessment}
                      enrollment={getEnrollmentForAssessment(assessment.id)}
                      onStart={handleStartAssessment}
                      onViewScore={handleViewScore}
                      isStarting={startingAssessmentId === assessment.id.toString()}
                    />
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                No available assessments found.
              </Typography>
            )}
          </Box>
        )
      })()}

      {/* Completed Assessments Section */}
      {(() => {
        return (
          <Box mb={4}>
            <Typography 
              variant="h5" 
              fontWeight="bold" 
              mb={2} 
              sx={{ color: theme.palette.success.main }}
            >
              Completed Assessments ({completedAssessments.length})
            </Typography>
            {completedAssessments.length > 0 ? (
              <Grid container spacing={3}>
                {completedAssessments.map((assessment) => (
                  <Grid item xs={12} sm={6} md={4} key={assessment.id}>
                    <ModernAssessmentCard
                      assessment={assessment}
                      enrollment={getEnrollmentForAssessment(assessment.id)}
                      onStart={handleStartAssessment}
                      onViewScore={handleViewScore}
                      isStarting={startingAssessmentId === assessment.id.toString()}
                    />
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                No completed assessments found.
              </Typography>
            )}
          </Box>
        )
      })()}

      {userEnrolledAssessments.length === 0 && (
        <Box textAlign="center" py={8}>
          <AssessmentIcon sx={{ fontSize: 80, color: theme.palette.grey[300], mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No assessments assigned to you
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Check back later for new assessment assignments
          </Typography>
        </Box>
      )}

      {/* Score Dialog */}
      <Dialog 
        open={scoreDialogOpen} 
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            {selectedAssessment?.user_submission_status?.has_passed ? (
              <CheckCircleIcon color="success" />
            ) : (
              <CancelIcon color="error" />
            )}
            Assessment Results
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedAssessment && (
            <Box>
              <Typography variant="h6" gutterBottom>
                {selectedAssessment.title}
              </Typography>
              
              {selectedAssessment.user_submission_status?.best_submission ? (
                <>
                  <Box textAlign="center" my={3}>
                    <Typography 
                      variant="h2" 
                      fontWeight="bold"
                      color={selectedAssessment.user_submission_status.has_passed ? 'success.main' : 'error.main'}
                      gutterBottom
                    >
                      {selectedAssessment.user_submission_status.best_score?.toFixed(1)}%
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Your Best Score
                    </Typography>
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  <Box display="flex" justifyContent="space-around" my={2}>
                    <Box textAlign="center">
                      <Typography variant="h5" fontWeight="bold">
                        {selectedAssessment.user_submission_status.best_submission.points_earned || 0}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Points Earned
                      </Typography>
                    </Box>
                    <Box textAlign="center">
                      <Typography variant="h5" fontWeight="bold">
                        {selectedAssessment.user_submission_status.best_submission.total_points || 0}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Total Points
                      </Typography>
                    </Box>
                    <Box textAlign="center">
                      <Typography variant="h5" fontWeight="bold">
                        {selectedAssessment.min_pass_percentage || 60}%
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Pass Required
                      </Typography>
                    </Box>
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Status: <Chip 
                        label={selectedAssessment.user_submission_status.has_passed ? 'Passed' : 'Not Passed'} 
                        color={selectedAssessment.user_submission_status.has_passed ? 'success' : 'error'}
                        size="small"
                        sx={{ ml: 1 }}
                      />
                    </Typography>
                    {selectedAssessment.user_submission_status.total_attempts && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Total Attempts: {selectedAssessment.user_submission_status.total_attempts}
                      </Typography>
                    )}
                    {selectedAssessment.user_submission_status.best_submission.submitted_at && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Completed: {new Date(selectedAssessment.user_submission_status.best_submission.submitted_at).toLocaleString()}
                      </Typography>
                    )}
                  </Box>

                  {!selectedAssessment.user_submission_status.has_passed && (
                    <Alert severity="info" sx={{ mt: 2 }}>
                      You can retake this assessment to improve your score. Your best score will be recorded.
                    </Alert>
                  )}
                </>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No submission data available.
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} variant="outlined">
            Close
          </Button>
          {(() => {
            const enrollment = getEnrollmentForAssessment(selectedAssessment?.id)
            const canShowResults = enrollment?.assignment?.show_results && selectedAssessment?.user_submission_status?.has_submission
            return canShowResults && (
              <Button 
                onClick={() => {
                  handleCloseDialog()
                  handleShowResults(selectedAssessment.id, selectedAssessment.title)
                }}
                variant="outlined"
                color="info"
                startIcon={<VisibilityIcon />}
              >
                Show Results
              </Button>
            )
          })()}
          <Button 
            onClick={handleRetakeAssessment} 
            variant="contained"
            color="primary"
          >
            Retake Assessment
          </Button>
        </DialogActions>
      </Dialog>

      {/* Show Results Dialog */}
      {selectedResultsAssessment && (
        <ShowResultsDialog
          open={showResultsDialogOpen}
          onClose={handleCloseResultsDialog}
          assessmentId={selectedResultsAssessment.id}
          assessmentTitle={selectedResultsAssessment.title}
        />
      )}
    </Box>
  )
}

export default ModernAssessmentCatalogPage